// MaterialGraphNode.jsx — Styled node component with outputs-first layout.
// Includes pin render, inline editors, and expose controls.
// Uses design's .sg-* classes and DOM structure.

import React, { useMemo, useRef, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { NODE_TYPES } from '../../lib/materialNodes'
import { categoryOf, categoryGlow, pinColor, CAT_COLORS } from './materialGraphTheme'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { TexturePicker } from './TextureSlotField'
import './materialGraph.css'

// ─── Render a single output pin (right side) ───────────────────────
function OutputPin({ output, nodeType }) {
  const color = pinColor(output.type)
  return (
    <div key={output.handle} className="sg-row sg-row-out">
      <div className="sg-rowspace" />
      <span className="sg-rowlabel sg-rowlabel-out">{output.handle}</span>
      <Handle
        type="source"
        position={Position.Right}
        id={output.handle}
        style={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          border: `1.5px solid var(--node-bg)`,
          boxShadow: `0 0 0 1.5px ${color}`,
          background: color,
          zIndex: 3,
        }}
      />
    </div>
  )
}

// ─── Inline editors for input values ───────────────────────────────

// Deterministic numeric field. Renders its OWN ▲/▼ stepper instead of the
// browser's native spin buttons: the native spinner's mouse auto-repeat gets
// stuck when React rewrites the controlled `value` mid-spin (every node edit
// flows through the modal's async persist + material reload), so a single
// click runs away incrementing non-stop. One click here = exactly one step;
// keyboard up/down on the input still works (discrete events, unaffected).
function NumberField({ value, onChange, step = 0.01, min, max, title, style }) {
  const v = typeof value === 'number' ? value : 0
  const clamp = (n) => {
    if (typeof min === 'number') n = Math.max(min, n)
    if (typeof max === 'number') n = Math.min(max, n)
    return Math.round(n * 1e6) / 1e6 // tame float drift from repeated stepping
  }
  const stepBtn = (dir, glyph) => (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => { e.stopPropagation(); onChange(clamp(v + dir * step)) }}
      title={dir > 0 ? 'Increase' : 'Decrease'}
    >{glyph}</button>
  )
  return (
    <div className="sg-num" onMouseDown={e => e.stopPropagation()} style={style}>
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        className="sg-val"
        value={v}
        onChange={e => { const n = parseFloat(e.target.value); onChange(Number.isFinite(n) ? clamp(n) : 0) }}
        onMouseDown={e => e.stopPropagation()}
        title={title}
        style={{ flex: 1, minWidth: 0 }}
      />
      <span className="sg-stepper">
        {stepBtn(1, '▲')}
        {stepBtn(-1, '▼')}
      </span>
    </div>
  )
}

function FloatEditor({ value, onChange }) {
  return <NumberField value={value} onChange={onChange} />
}

// Vec3 compact editor — a color swatch that opens the native color picker.
// A transparent <input type="color"> overlays the swatch so clicking it pops
// the OS picker (Unreal's color-expression UX). Values are linear 0..1 floats.
function Vec3Editor({ value, onChange }) {
  const arr = Array.isArray(value) ? value : [0, 0, 0]
  const clamp01 = v => Math.min(1, Math.max(0, v ?? 0))
  const [r, g, b] = arr.slice(0, 3).map(v => Math.round(clamp01(v) * 255))
  const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
  return (
    <div
      className="sg-swatch"
      style={{ background: hex, position: 'relative', cursor: 'pointer' }}
      title="Pick color"
      onMouseDown={e => e.stopPropagation()}
    >
      <input
        type="color"
        value={hex}
        onChange={e => {
          const h = e.target.value
          onChange([
            parseInt(h.slice(1, 3), 16) / 255,
            parseInt(h.slice(3, 5), 16) / 255,
            parseInt(h.slice(5, 7), 16) / 255,
          ])
        }}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          opacity: 0, cursor: 'pointer', border: 'none', padding: 0, margin: 0,
        }}
      />
    </div>
  )
}

// Texture slot rendered on nodes with `hasTexture` (e.g. Texture Sample).
// The binding is node.data.textureId, not a wired pin — clicking the thumbnail
// opens the shared TexturePicker (same popup the Material Inspector uses).
function TextureSlot({ textureId, texById, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const tex = textureId ? texById?.get(textureId) : null
  const previewUrl = tex?.file_url ?? null
  return (
    // sg-row-tall, not sg-row: the plain row is a HARD `height: var(--row-h)`
    // (19px) and this swatch is 28px, so it bled out of its own row and
    // overlapped the colour-space dropdown above it by 4px (measured). The row
    // grows rather than the swatch shrinking — the thumbnail IS the control,
    // and at 19px there is no recognising a texture in it.
    <div className="sg-row sg-row-tall sg-row-in" onMouseDown={e => e.stopPropagation()}>
      <span className="sg-rowlabel" style={{ color: 'var(--fg-2)' }}>texture</span>
      <span className="sg-rowspace" />
      <div
        ref={ref}
        className={`sg-texslot${open ? ' sg-texslot-open' : ''}`}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        title={tex?.name ?? 'Pick a texture'}
        style={{
          width: 28, height: 28, borderRadius: 3, flexShrink: 0, cursor: 'pointer',
          border: '1px solid var(--line-strong)',
          background: previewUrl
            ? `url(${previewUrl}) center / cover`
            : 'repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 8px 8px',
        }}
      />
      {open && (
        <TexturePicker
          anchorRef={ref}
          textures={CustomAssetManager.listTextureAssets()}
          current={textureId}
          onPick={(id) => { onChange(id); setOpen(false) }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  )
}

function Vec2Editor({ value, onChange }) {
  const arr = Array.isArray(value) ? value : [0, 0]
  // For vec2, show as a small indicator (could be extended with detailed edit in inspector)
  return (
    <div style={{ display: 'flex', gap: 4, flex: 1 }}>
      <NumberField
        value={typeof arr[0] === 'number' ? arr[0] : 0}
        onChange={x => onChange([x, arr[1]])}
        title="X"
        style={{ flex: 1 }}
      />
      <NumberField
        value={typeof arr[1] === 'number' ? arr[1] : 0}
        onChange={y => onChange([arr[0], y])}
        title="Y"
        style={{ flex: 1 }}
      />
    </div>
  )
}

function TextureEditor({ value, texById, onChange }) {
  const tex = value ? texById?.get(value) : null
  const texName = tex?.name ?? '(none)'
  return (
    <div
      className="sg-val"
      title="Texture picker not yet implemented"
      style={{
        width: 'auto',
        minWidth: '48px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
      }}
    >
      {texName}
    </div>
  )
}

// Channel toggles for the Component Mask node. Each channel is "on" unless
// node.data.mask_<c> is explicitly false (absent = on → fresh node passes all
// through, matching the compiler's emit). Output arity = number of on channels.
function MaskChannels({ data, channels, onToggle }) {
  const isOn = (c) => data?.[`mask_${c}`] !== false
  return (
    <div className="sg-row sg-row-in" onMouseDown={e => e.stopPropagation()}>
      <span className="sg-rowlabel" style={{ color: 'var(--fg-2)' }}>mask</span>
      <span className="sg-rowspace" />
      <div style={{ display: 'flex', gap: 3 }}>
        {channels.map(c => {
          const on = isOn(c)
          return (
            <button
              key={c}
              onMouseDown={e => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onToggle(c, !on) }}
              title={`Toggle ${c.toUpperCase()} channel`}
              style={{
                width: 18, height: 16, fontSize: 9, fontFamily: 'var(--m-font)',
                borderRadius: 3, cursor: 'pointer', padding: 0,
                border: '1px solid ' + (on ? 'var(--accent)' : 'var(--line-strong)'),
                background: on ? 'color-mix(in srgb, var(--accent) 30%, transparent)' : 'var(--slot-bg)',
                color: on ? 'var(--fg)' : 'var(--fg-3)',
              }}
            >{c.toUpperCase()}</button>
          )
        })}
      </div>
    </div>
  )
}

// Color-ramp gradient editor (Blender ColorRamp). Edits node.data.stops =
// [{ pos, color:[r,g,b] }]. Shows a gradient bar + a row per stop (color
// picker + position) with add/remove. Compiler bakes these into mix() GLSL.
const DEFAULT_RAMP_STOPS = [{ pos: 0, color: [0, 0, 0] }, { pos: 1, color: [1, 1, 1] }]
function rampHex(c) {
  const [r, g, b] = (Array.isArray(c) ? c : [0, 0, 0]).slice(0, 3).map(v => Math.round(Math.min(1, Math.max(0, v ?? 0)) * 255))
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
}
function hexToRgb01(h) {
  return [parseInt(h.slice(1, 3), 16) / 255, parseInt(h.slice(3, 5), 16) / 255, parseInt(h.slice(5, 7), 16) / 255]
}
function ColorRampEditor({ stops, onChange }) {
  const list = Array.isArray(stops) && stops.length >= 2 ? stops : DEFAULT_RAMP_STOPS
  const sorted = [...list].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0))
  const grad = `linear-gradient(to right, ${sorted.map(s => `${rampHex(s.color)} ${Math.round((s.pos ?? 0) * 100)}%`).join(', ')})`
  const setStop = (idx, patch) => onChange(list.map((s, i) => i === idx ? { ...s, ...patch } : s))
  const addStop = () => onChange([...list, { pos: 0.5, color: [0.5, 0.5, 0.5] }])
  const removeStop = (idx) => { if (list.length > 2) onChange(list.filter((_, i) => i !== idx)) }
  const stopBtn = (disabled) => ({
    width: 16, height: 16, borderRadius: 3, padding: 0, flexShrink: 0,
    fontSize: 10, lineHeight: '14px', cursor: disabled ? 'default' : 'pointer',
    border: '1px solid var(--line-strong)', background: 'var(--slot-bg)',
    color: disabled ? 'var(--fg-3)' : '#e08080',
  })
  return (
    <div
      className="sg-row sg-row-in"
      style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, height: 'auto' }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div style={{ height: 14, borderRadius: 3, border: '1px solid var(--line-strong)', background: grad }} />
      {list.map((s, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div
            style={{ position: 'relative', width: 18, height: 14, borderRadius: 3, flexShrink: 0, border: '1px solid var(--line-strong)', background: rampHex(s.color), cursor: 'pointer' }}
            title="Stop color"
          >
            <input
              type="color"
              value={rampHex(s.color)}
              onChange={e => setStop(i, { color: hexToRgb01(e.target.value) })}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
            />
          </div>
          <NumberField
            value={typeof s.pos === 'number' ? s.pos : 0}
            onChange={pos => setStop(i, { pos })}
            min={0} max={1} step={0.05}
            title="Position"
            style={{ flex: 1 }}
          />
          <button
            onClick={e => { e.stopPropagation(); removeStop(i) }}
            onMouseDown={e => e.stopPropagation()}
            disabled={list.length <= 2}
            title="Remove stop"
            style={stopBtn(list.length <= 2)}
          >×</button>
        </div>
      ))}
      <button
        onClick={e => { e.stopPropagation(); addStop() }}
        onMouseDown={e => e.stopPropagation()}
        title="Add stop"
        style={{
          fontFamily: 'var(--m-font)', fontSize: 10, padding: '2px 6px', borderRadius: 3,
          border: '1px solid var(--line-strong)', background: 'var(--slot-bg)',
          color: 'var(--fg-2)', cursor: 'pointer',
        }}
      >+ stop</button>
    </div>
  )
}

// Dropdown for a node's enum field (e.g. Noise `type`). Driven by node.data[key].
function EnumField({ value, options, onChange }) {
  return (
    <select
      className="sg-select"
      value={value}
      onChange={e => onChange(e.target.value)}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

// Checkbox for a node's boolean field (e.g. Noise `normalize`).
function BoolField({ checked, label, onChange }) {
  return (
    <label className="sg-bool" onMouseDown={e => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      />
      <span>{label}</span>
    </label>
  )
}

// ─── Render a single input pin (left side) with editor ─────────────
function InputPin({
  input,
  nodeData,
  nodeId,
  onDataChange,
  onExposeChange,
}) {
  const textureAssets = CustomAssetManager.listTextureAssets()
  const texById = useMemo(() => {
    const m = new Map()
    for (const t of textureAssets) m.set(t.id, t)
    return m
  }, [textureAssets.length])

  const currentValue = nodeData[input.handle] ?? input.default
  const color = pinColor(input.type)
  const isConnected = false // TODO: check if this pin is wired (would need edges prop)

  const handleChange = (value) => {
    if (onDataChange) onDataChange(value)
  }

  // Inputs that fall back to the mesh UVs when unwired (Noise/TextureSample/
  // NormalMap `uv`) ignore any inline value — show a "mesh UV" hint instead of a
  // dead editor so the field doesn't imply (0,0) does something.
  const usesMeshUv = !!input.usesMeshUv && !isConnected

  // Render editor inline for float/vec types when not connected
  const shouldShowEditor = !isConnected && !usesMeshUv && ['float', 'vec2', 'vec3', 'sampler2D'].includes(input.type)

  return (
    <>
      {/* Pin label row */}
      <div className="sg-row sg-row-in">
        <Handle
          type="target"
          position={Position.Left}
          id={input.handle}
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            border: `1.5px solid var(--node-bg)`,
            boxShadow: `0 0 0 1.5px ${color}`,
            background: color,
            zIndex: 3,
          }}
        />
        <span className="sg-rowlabel" style={{ color: isConnected ? 'var(--fg)' : 'var(--fg-2)' }}>
          {input.handle}
        </span>
        <span className="sg-rowspace" />
        {/* Mesh-UV inputs: hint instead of a dead inline editor */}
        {usesMeshUv && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--fg-3)', fontStyle: 'italic' }}>
            mesh UV
          </span>
        )}
        {/* Show editor inline if not connected */}
        {shouldShowEditor && input.type === 'float' && (
          <FloatEditor value={currentValue} onChange={handleChange} />
        )}
        {shouldShowEditor && input.type === 'vec3' && (
          <Vec3Editor value={currentValue} onChange={handleChange} />
        )}
        {shouldShowEditor && input.type === 'vec2' && (
          <Vec2Editor value={currentValue} onChange={handleChange} />
        )}
        {shouldShowEditor && input.type === 'sampler2D' && (
          <TextureEditor value={currentValue} texById={texById} onChange={handleChange} />
        )}
      </div>
    </>
  )
}

// ─── Main node component ──────────────────────────────────────────
export default function MaterialGraphNode({ data, id, selected, updateNodeData }) {
  const registry = NODE_TYPES[data.nodeType]
  if (!registry) {
    return (
      <div className="sg-node" style={{ padding: 8, color: '#f87171' }}>
        Unknown: {data.nodeType}
      </div>
    )
  }

  const category = categoryOf(data.nodeType)
  const catColor = CAT_COLORS[category] || CAT_COLORS.param
  const isParam = !!registry.paramable && !!data.param

  const textureAssets = CustomAssetManager.listTextureAssets()
  const texById = useMemo(() => {
    const m = new Map()
    for (const t of textureAssets) m.set(t.id, t)
    return m
  }, [textureAssets.length])

  return (
    <div
      className={`sg-node ${selected ? 'is-selected' : ''}`}
      style={{
        width: 168, // Design spec node width
      }}
    >
      {/* Header with category dot + title */}
      <div
        className="sg-node-hd"
        style={{
          background: `linear-gradient(180deg, color-mix(in srgb, ${catColor} 22%, var(--node-bg)) 0%, var(--node-bg) 100%)`,
        }}
      >
        {/* Category dot */}
        <span className="sg-cat-dot" style={{ background: catColor, color: catColor }} />
        {/* Title */}
        <span className="sg-node-title">{registry.label}</span>
        {isParam && (
          <span
            title="Parameter — same name + type means the same parameter"
            style={{
              marginLeft: 'auto', fontFamily: 'var(--m-font)', fontSize: 8, letterSpacing: '.08em',
              padding: '1px 4px', borderRadius: 3, color: 'var(--accent-2, #b7a2ff)',
              border: '1px solid var(--accent)', background: 'color-mix(in srgb, var(--accent) 18%, transparent)',
            }}
          >PARAM</span>
        )}
      </div>

      {/* Body: outputs (top), then inputs (bottom) */}
      <div className="sg-node-body">
        {/* Parameter name (Unreal-style) — converted Constants publish a named param */}
        {isParam && updateNodeData && (
          <div className="sg-row sg-row-in" onMouseDown={e => e.stopPropagation()}>
            <span className="sg-rowlabel" style={{ color: 'var(--fg-2)' }}>name</span>
            <span className="sg-rowspace" />
            <input
              className="sg-val"
              value={data.paramName ?? 'Param'}
              onChange={e => updateNodeData('paramName', e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              placeholder="Param"
              title="Parameter name — reuse the same name to share one parameter"
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>
        )}

        {/* Outputs first (right side) */}
        {registry.outputs && registry.outputs.length > 0 && (
          <>
            {registry.outputs.map(output => (
              <OutputPin key={output.handle} output={output} nodeType={data.nodeType} />
            ))}
          </>
        )}

        {/* Enum dropdowns (e.g. Noise type) */}
        {registry.enums && updateNodeData && registry.enums.map(en => (
          <div key={en.key} className="sg-row sg-row-in" onMouseDown={e => e.stopPropagation()}>
            <EnumField
              value={data[en.key] ?? en.default}
              options={en.options}
              onChange={(v) => updateNodeData(en.key, v)}
            />
          </div>
        ))}

        {/* Boolean toggles (e.g. Noise normalize) */}
        {registry.bools && updateNodeData && registry.bools.map(b => (
          <div key={b.key} className="sg-row sg-row-in" onMouseDown={e => e.stopPropagation()}>
            <BoolField
              checked={data[b.key] ?? b.default}
              label={b.label}
              onChange={(v) => updateNodeData(b.key, v)}
            />
          </div>
        ))}

        {/* Texture slot (node.data.textureId) for nodes that sample a texture */}
        {registry.hasTexture && updateNodeData && (
          <TextureSlot
            textureId={data.textureId ?? null}
            texById={texById}
            onChange={(id) => updateNodeData('textureId', id)}
          />
        )}

        {/* Channel toggles for Component Mask */}
        {registry.maskChannels && updateNodeData && (
          <MaskChannels
            data={data}
            channels={registry.maskChannels}
            onToggle={(c, v) => updateNodeData(`mask_${c}`, v)}
          />
        )}

        {/* Gradient editor for Color Ramp */}
        {registry.hasColorRamp && updateNodeData && (
          <ColorRampEditor
            stops={data.stops}
            onChange={(next) => updateNodeData('stops', next)}
          />
        )}

        {/* Inputs last (left side with editors) */}
        {registry.inputs && registry.inputs.length > 0 && (
          <>
            {registry.inputs.map(input => (
              <InputPin
                key={input.handle}
                input={input}
                nodeData={data}
                nodeId={data.nodeId}
                onDataChange={
                  updateNodeData
                    ? (value) => updateNodeData(input.handle, value)
                    : null
                }
                onExposeChange={
                  updateNodeData
                    ? (exposeObj) => updateNodeData('__expose__', exposeObj)
                    : null
                }
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}
